import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import { 
  Save, 
  Plus, 
  Trash2, 
  ArrowUp, 
  ArrowDown,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
  Globe
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminGeneral = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    socialNetworks: [],
    seo: {},
    status: { isActive: true, comingSoonTitle: '', comingSoonDescription: '' },
    media: { cdnBaseUrl: '' },
    features: { reviewsEnabled: true }
  });

  const [newSocial, setNewSocial] = useState({ name: '', url: '', icon: 'Globe' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API}/general-settings/general/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      setSettings({
        socialNetworks: data.socialNetworks || [],
        seo: data.seo || {},
        status: data.status || { isActive: true, comingSoonTitle: 'Próximamente', comingSoonDescription: 'Estamos trabajando en algo increíble. ¡Vuelve pronto!' },
        media: data.media || { cdnBaseUrl: '' },
        features: data.features || { reviewsEnabled: true }
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load settings',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`${API}/general-settings/general`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Success',
        description: 'Settings saved successfully'
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  // Social Networks handlers
  const addSocialNetwork = () => {
    if (!newSocial.name || !newSocial.url) {
      toast({
        title: 'Validation Error',
        description: 'Name and URL are required',
        variant: 'destructive'
      });
      return;
    }

    const newNetwork = {
      id: Date.now().toString(),
      ...newSocial,
      enabled: true,
      order: settings.socialNetworks.length
    };

    setSettings({
      ...settings,
      socialNetworks: [...settings.socialNetworks, newNetwork]
    });
    setNewSocial({ name: '', url: '', icon: 'Globe' });
  };

  const deleteSocialNetwork = (id) => {
    setSettings({
      ...settings,
      socialNetworks: settings.socialNetworks.filter(sn => sn.id !== id)
    });
  };

  const toggleSocialNetwork = (id, newValue) => {
    setSettings({
      ...settings,
      socialNetworks: settings.socialNetworks.map(sn =>
        sn.id === id ? { ...sn, enabled: newValue } : sn
      )
    });
  };

  const moveSocialNetwork = (id, direction) => {
    const index = settings.socialNetworks.findIndex(sn => sn.id === id);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === settings.socialNetworks.length - 1)
    ) {
      return;
    }

    const newNetworks = [...settings.socialNetworks];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newNetworks[index], newNetworks[newIndex]] = [newNetworks[newIndex], newNetworks[index]];
    
    // Update order values
    newNetworks.forEach((sn, idx) => {
      sn.order = idx;
    });

    setSettings({ ...settings, socialNetworks: newNetworks });
  };

  const getIconComponent = (iconName) => {
    const icons = {
      Facebook, Instagram, Twitter, Youtube, Linkedin, Globe
    };
    const Icon = icons[iconName] || Globe;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">General Settings</h1>
          <p className="text-gray-600 mt-1">Manage your site's general configuration</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Tabs defaultValue="social" className="w-full">
        <TabsList>
          <TabsTrigger value="social">Social Networks</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
          <TabsTrigger value="status">Status</TabsTrigger>
          <TabsTrigger value="media">Media / CDN</TabsTrigger>
          <TabsTrigger value="features" data-testid="admin-general-features-tab">Features</TabsTrigger>
        </TabsList>

        {/* Social Networks Tab */}
        <TabsContent value="social" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Social Networks</CardTitle>
              <CardDescription>
                Manage social network links displayed in the footer
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Add New Social Network */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="font-semibold mb-3">Add New Social Network</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <Input
                    placeholder="Name (e.g., Facebook)"
                    value={newSocial.name}
                    onChange={(e) => setNewSocial({ ...newSocial, name: e.target.value })}
                  />
                  <Input
                    placeholder="URL"
                    value={newSocial.url}
                    onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                  />
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                    value={newSocial.icon}
                    onChange={(e) => setNewSocial({ ...newSocial, icon: e.target.value })}
                  >
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Twitter">Twitter</option>
                    <option value="Youtube">Youtube</option>
                    <option value="Linkedin">Linkedin</option>
                    <option value="Globe">Other (Globe)</option>
                  </select>
                  <Button onClick={addSocialNetwork}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Social Networks Table */}
              {settings.socialNetworks.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Icon</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>URL</TableHead>
                      <TableHead>Enabled</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {settings.socialNetworks.map((social, index) => (
                      <TableRow key={social.id}>
                        <TableCell>{getIconComponent(social.icon)}</TableCell>
                        <TableCell>{social.name}</TableCell>
                        <TableCell className="max-w-xs truncate">{social.url}</TableCell>
                        <TableCell>
                          <Switch
                            checked={social.enabled}
                            onCheckedChange={(checked) => toggleSocialNetwork(social.id, checked)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveSocialNetwork(social.id, 'up')}
                              disabled={index === 0}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => moveSocialNetwork(social.id, 'down')}
                              disabled={index === settings.socialNetworks.length - 1}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSocialNetwork(social.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No social networks added yet. Add one above to get started.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Basic Meta Tags</CardTitle>
              <CardDescription>Configure basic SEO meta tags</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="siteName">Site Name</Label>
                  <Input
                    id="siteName"
                    value={settings.seo.siteName || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, siteName: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="author">Author</Label>
                  <Input
                    id="author"
                    value={settings.seo.author || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, author: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="defaultMetaTitle">Default Meta Title</Label>
                <Input
                  id="defaultMetaTitle"
                  value={settings.seo.defaultMetaTitle || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, defaultMetaTitle: e.target.value }
                  })}
                />
              </div>

              <div>
                <Label htmlFor="defaultMetaDescription">Default Meta Description</Label>
                <Textarea
                  id="defaultMetaDescription"
                  rows={3}
                  value={settings.seo.defaultMetaDescription || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, defaultMetaDescription: e.target.value }
                  })}
                />
              </div>

              <div>
                <Label htmlFor="metaKeywords">Meta Keywords (comma separated)</Label>
                <Input
                  id="metaKeywords"
                  value={settings.seo.metaKeywords || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, metaKeywords: e.target.value }
                  })}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="robotsIndex"
                  checked={settings.seo.robotsIndex ?? true}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, robotsIndex: checked }
                  })}
                />
                <Label htmlFor="robotsIndex">Allow Search Engine Indexing</Label>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Open Graph (OG) Tags</CardTitle>
              <CardDescription>Configure Open Graph tags for social media sharing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ogTitle">OG Title</Label>
                  <Input
                    id="ogTitle"
                    value={settings.seo.ogTitle || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, ogTitle: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="ogType">OG Type</Label>
                  <Input
                    id="ogType"
                    value={settings.seo.ogType || 'website'}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, ogType: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="ogDescription">OG Description</Label>
                <Textarea
                  id="ogDescription"
                  rows={3}
                  value={settings.seo.ogDescription || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, ogDescription: e.target.value }
                  })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ogImage">OG Image URL</Label>
                  <Input
                    id="ogImage"
                    placeholder="https://example.com/image.jpg"
                    value={settings.seo.ogImage || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, ogImage: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="ogSiteName">OG Site Name</Label>
                  <Input
                    id="ogSiteName"
                    value={settings.seo.ogSiteName || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, ogSiteName: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="ogLocale">OG Locale</Label>
                <Input
                  id="ogLocale"
                  placeholder="es_CO"
                  value={settings.seo.ogLocale || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, ogLocale: e.target.value }
                  })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Twitter Card Tags</CardTitle>
              <CardDescription>Configure Twitter Card meta tags</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="twitterCardType">Card Type</Label>
                  <select
                    id="twitterCardType"
                    className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                    value={settings.seo.twitterCardType || 'summary_large_image'}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, twitterCardType: e.target.value }
                    })}
                  >
                    <option value="summary">Summary</option>
                    <option value="summary_large_image">Summary Large Image</option>
                    <option value="app">App</option>
                    <option value="player">Player</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="twitterSite">Twitter Site (@username)</Label>
                  <Input
                    id="twitterSite"
                    placeholder="@yourhandle"
                    value={settings.seo.twitterSite || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, twitterSite: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="twitterCreator">Twitter Creator (@username)</Label>
                  <Input
                    id="twitterCreator"
                    placeholder="@creator"
                    value={settings.seo.twitterCreator || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, twitterCreator: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="twitterImage">Twitter Image URL</Label>
                  <Input
                    id="twitterImage"
                    placeholder="https://example.com/twitter-image.jpg"
                    value={settings.seo.twitterImage || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, twitterImage: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="twitterTitle">Twitter Title</Label>
                <Input
                  id="twitterTitle"
                  value={settings.seo.twitterTitle || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, twitterTitle: e.target.value }
                  })}
                />
              </div>

              <div>
                <Label htmlFor="twitterDescription">Twitter Description</Label>
                <Textarea
                  id="twitterDescription"
                  rows={2}
                  value={settings.seo.twitterDescription || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, twitterDescription: e.target.value }
                  })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Favicon & Icons</CardTitle>
              <CardDescription>Configure site icons and manifest</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="faviconUrl">Favicon URL</Label>
                <Input
                  id="faviconUrl"
                  placeholder="https://example.com/favicon.ico"
                  value={settings.seo.faviconUrl || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, faviconUrl: e.target.value }
                  })}
                />
              </div>

              <div>
                <Label htmlFor="appleTouchIconUrl">Apple Touch Icon URL</Label>
                <Input
                  id="appleTouchIconUrl"
                  placeholder="https://example.com/apple-touch-icon.png"
                  value={settings.seo.appleTouchIconUrl || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, appleTouchIconUrl: e.target.value }
                  })}
                />
              </div>

              <div>
                <Label htmlFor="manifestUrl">Manifest URL</Label>
                <Input
                  id="manifestUrl"
                  placeholder="https://example.com/manifest.json"
                  value={settings.seo.manifestUrl || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, manifestUrl: e.target.value }
                  })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Analytics & Tracking</CardTitle>
              <CardDescription>Configure analytics and tracking IDs</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="googleAnalyticsId">Google Analytics ID (GA4)</Label>
                  <Input
                    id="googleAnalyticsId"
                    placeholder="G-XXXXXXXXXX"
                    value={settings.seo.googleAnalyticsId || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, googleAnalyticsId: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="googleTagManagerId">Google Tag Manager ID</Label>
                  <Input
                    id="googleTagManagerId"
                    placeholder="GTM-XXXXXXX"
                    value={settings.seo.googleTagManagerId || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, googleTagManagerId: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="facebookPixelId">Facebook Pixel ID</Label>
                  <Input
                    id="facebookPixelId"
                    placeholder="XXXXXXXXXXXXXXXX"
                    value={settings.seo.facebookPixelId || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, facebookPixelId: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="microsoftClarityId">Microsoft Clarity ID</Label>
                  <Input
                    id="microsoftClarityId"
                    placeholder="XXXXXXXXXX"
                    value={settings.seo.microsoftClarityId || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, microsoftClarityId: e.target.value }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="hotjarId">Hotjar ID</Label>
                <Input
                  id="hotjarId"
                  placeholder="XXXXXXX"
                  value={settings.seo.hotjarId || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, hotjarId: e.target.value }
                  })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Schema.org Organization</CardTitle>
              <CardDescription>Configure organization structured data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="organizationName">Organization Name</Label>
                  <Input
                    id="organizationName"
                    value={settings.seo.organizationName || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, organizationName: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label htmlFor="organizationLogo">Organization Logo URL</Label>
                  <Input
                    id="organizationLogo"
                    placeholder="https://example.com/logo.png"
                    value={settings.seo.organizationLogo || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      seo: { ...settings.seo, organizationLogo: e.target.value }
                    })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Advanced Settings</CardTitle>
              <CardDescription>Configure advanced SEO settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="canonicalUrlBase">Canonical URL Base</Label>
                <Input
                  id="canonicalUrlBase"
                  placeholder="https://example.com"
                  value={settings.seo.canonicalUrlBase || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, canonicalUrlBase: e.target.value }
                  })}
                />
              </div>

              <div>
                <Label htmlFor="defaultLanguage">Default Language</Label>
                <Input
                  id="defaultLanguage"
                  placeholder="es"
                  value={settings.seo.defaultLanguage || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    seo: { ...settings.seo, defaultLanguage: e.target.value }
                  })}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Status Tab */}
        <TabsContent value="status" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>E-commerce Status</CardTitle>
              <CardDescription>
                Control whether your e-commerce is active or in maintenance mode
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-semibold">E-commerce Active</h3>
                  <p className="text-sm text-gray-600">
                    {settings.status.isActive 
                      ? 'Your site is currently active and visible to customers' 
                      : 'Your site is in maintenance mode - showing "Coming Soon" page'}
                  </p>
                </div>
                <Switch
                  checked={settings.status.isActive}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    status: { ...settings.status, isActive: checked }
                  })}
                />
              </div>

              {!settings.status.isActive && (
                <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4 rounded">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Note:</strong> Customers will see the "Coming Soon" page. 
                    Admins can still access the admin panel.
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="comingSoonTitle">Coming Soon Title</Label>
                <Input
                  id="comingSoonTitle"
                  value={settings.status.comingSoonTitle}
                  onChange={(e) => setSettings({
                    ...settings,
                    status: { ...settings.status, comingSoonTitle: e.target.value }
                  })}
                />
              </div>

              <div>
                <Label htmlFor="comingSoonDescription">Coming Soon Description</Label>
                <Textarea
                  id="comingSoonDescription"
                  rows={4}
                  value={settings.status.comingSoonDescription}
                  onChange={(e) => setSettings({
                    ...settings,
                    status: { ...settings.status, comingSoonDescription: e.target.value }
                  })}
                />
              </div>

              {/* Preview */}
              <div className="border rounded-lg p-6 bg-gray-50">
                <h4 className="font-semibold mb-3">Preview</h4>
                <div className="bg-white rounded-lg p-8 text-center">
                  <img 
                    src="/logo-cimonamia.png" 
                    alt="Logo" 
                    className="h-12 mx-auto mb-6 invert"
                  />
                  <h2 className="text-3xl font-bold mb-4">
                    {settings.status.comingSoonTitle}
                  </h2>
                  <p className="text-gray-600">
                    {settings.status.comingSoonDescription}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Media / CDN Tab */}
        <TabsContent value="media" className="space-y-4" data-testid="admin-general-media-tab">
          <Card>
            <CardHeader>
              <CardTitle>Media &amp; CDN</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Public host that serves uploaded images. The storefront and admin
                rewrite legacy <code>/api/media/serve/...</code> paths to this host
                in real time, so you can switch CDN providers without redeploying.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="cdn-base-url">CDN base URL</Label>
                <Input
                  id="cdn-base-url"
                  data-testid="admin-cdn-base-url"
                  placeholder="https://media.cimonamia.com"
                  value={settings.media?.cdnBaseUrl || ''}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      media: { ...(settings.media || {}), cdnBaseUrl: e.target.value },
                    })
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                  No agregues barra al final. Ej: <code>https://media.cimonamia.com</code>{' '}
                  o el dominio CloudFront por defecto.
                </p>
              </div>

              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <strong>Cambios en caliente:</strong> al guardar, el backend actualiza la
                URL devuelta por <code>/api/media/upload</code> sin reinicio.
                Para que el storefront del navegador refleje el nuevo host hay
                que recargar la página una vez.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features / Feature Toggles Tab */}
        <TabsContent value="features" className="space-y-4" data-testid="admin-general-features-content">
          <Card>
            <CardHeader>
              <CardTitle>Feature Toggles</CardTitle>
              <CardDescription>
                Enable or disable major storefront features from the CMS. Changes take effect
                immediately on the customer-facing site (a browser refresh may be required once).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Reviews toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-semibold">Customer Reviews</h3>
                  <p className="text-sm text-gray-600">
                    {settings.features.reviewsEnabled
                      ? 'Reviews tab and rating widgets are visible on product pages. Customers can submit new reviews.'
                      : 'The Reviews tab is hidden on product pages and the review submission endpoint rejects new posts.'}
                  </p>
                </div>
                <Switch
                  data-testid="reviews-enabled-toggle"
                  checked={settings.features.reviewsEnabled}
                  onCheckedChange={(checked) =>
                    setSettings({
                      ...settings,
                      features: { ...settings.features, reviewsEnabled: checked },
                    })
                  }
                />
              </div>

              {!settings.features.reviewsEnabled && (
                <div className="border-l-4 border-amber-500 bg-amber-50 p-4 rounded text-sm text-amber-900">
                  <strong>Note:</strong> Existing reviews stay in the database and remain
                  accessible via the admin moderation panel. They will simply not be shown
                  on the storefront while this toggle is off.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminGeneral;
